/*
 * Brora gift-wrap UI controller — the light-DOM launcher button + shared drawer that replace the "Wrapped"
 * app's injected shadow-DOM UI. Pairs with assets/gift-wrap.js (the engine) and
 * snippets/gift-wrap-drawer.liquid + gift-wrap-button.liquid.
 *
 * Entire-order gifting only (per-item + PDP gifting were removed — TASK-51690).
 *
 * Responsibilities:
 *   - open/close the shared <dialog> (focus trap, restore focus, Escape) and configure it for the whole-order
 *     gift from window.Wrapped.options — fields, labels, title;
 *   - validate via the theme's form-validation.js (data-validate → form:valid), then save/remove by driving
 *     the app's co-located <wrapped-gift-block> through the engine (which writes the cart directly);
 *   - append the gift fee to the order launcher when it isn't already rendered server-side.
 *
 * The launcher button is rendered in Liquid (snippets/gift-wrap-button.liquid), not here; the app's own
 * injected launchers are hidden by CSS (wrapped-gift-block { display: none !important }).
 *
 * DOM hooks (data-*): -open/-mode/-state (launcher); -drawer/-title/-subtitle/-banner(-img)/-fields/
 *   -save/-remove/-close/-form (drawer); -price-target/-save-text/-remove-text (label slots); -announce
 *   (aria-live). Events: listens mini-bag:updated / wrapped:cart-updated / cart:added; dispatches
 *   wrapped:cart-updated after a drawer save/remove.
 *
 * WARNING — coupling: reads window.Wrapped.options + drives the app's <wrapped-gift-block>. A Wrapped app
 * update can change either; the engine guards its reads and rejects/warns on drift.
 */
(function () {
  'use strict';

  const engine = () => window.BroraGiftWrap;

  // Liquid's `| t` HTML-escapes locale values (e.g. "&" → "&amp;"); decode before use as textContent.
  const decode = (s) => {
    const el = document.createElement('textarea');
    el.innerHTML = String(s == null ? '' : s);
    return el.value;
  };
  // Format the gift fee (config price is in MAJOR units, e.g. 5) through the shared market-aware
  // formatter. Only reached when Liquid did not render the fee server-side.
  const money = (major) => {
    const amount = major || 0;
    const bv = window.broraVoyado || {};
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || bv.currencyCode;
    const out = typeof bv.formatMoney === 'function' ? bv.formatMoney(amount, currency) : null;
    if (out != null) return out;
    try {
      return currency ? new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount) : String(amount);
    } catch (e) { return String(amount); }
  };
  const RAW = (typeof window !== 'undefined' && window.wrappedGiftStrings) || {};
  const str = (key, fallback) => (RAW[key] ? decode(RAW[key]) : fallback);

  // Drawer strings (bridged from theme settings / locale) with English fallbacks. The launcher labels
  // (Gift order / Edit wrapping) are rendered in Liquid by gift-wrap-button.liquid.
  const LABELS = {
    save: 'Save',
    titleOrder: str('titleOrder', 'Gift Order')
  };

  // The order drawer opens OVER the still-open mini-bag, which already holds the shared page scroll-lock
  // (data-scroll-lock on <html>). That lock is a boolean, not a counter — so on close the drawer must NOT
  // release it while the mini-bag still holds it, or the page unlocks beneath the open mini-bag.
  const miniBagOpen = () => {
    const mb = document.querySelector('[data-mini-bag]');
    return !!(mb && mb.getAttribute('data-mini-bag-open') === 'true');
  };

  // ── dialog element refs ────────────────────────────────────────────────────
  const $dialog = () => document.getElementById('gift-wrap-drawer');
  const ref = (name) => {
    const d = $dialog();
    return d && d.querySelector(`[data-gift-wrap-${name}]`);
  };
  const fieldEl = (key) => {
    const d = $dialog();
    return d && d.querySelector(`#gift-wrap-${key}`);
  };
  const FIELD_IDS = { receiver: 'receiver', sender: 'sender', message: 'message' };

  // ── open dialog context ────────────────────────────────────────────────────
  let ctx = null;          // { mode, editing, trigger }
  let trap = null;
  let lastFocus = null;
  let inFlight = false;    // a cart write (save/remove) is in flight — gate re-entrant save/remove so a rapid
                           // save→edit→remove can't run two concurrent app-block ops that deadlock each other.
  const OP_TIMEOUT_MS = 8000;  // hard ceiling so a hung app op still settles (the drawer never sticks open).

  // Configure the dialog for the whole-order gift: title, enabled/required/labelled fields, message
  // max-length, actions.
  function applyConfig() {
    const G = engine();
    const cfg = G && G.config('order');
    // Dialog title (Playfair, sentence case — NOT the uppercase launcher label): always "Gift Order".
    const title = ref('title');
    if (title) title.textContent = LABELS.titleOrder;

    // Banner (gift-box hero) + the gift-type/fee subtitle ("Gifting entire order (£5.00)").
    const bannerWrap = ref('banner');
    const bannerImg = ref('banner-img');
    if (bannerWrap && bannerImg) {
      // cfg.image is the app's banner URL (window.Wrapped.options) — run it through the theme's shared URL
      // allow-list (voyado-card-renderer.js) before assigning, so a spoofed non-image/scheme can't ride in.
      const safeImg = cfg && cfg.image ? (window.voyadoSafeUrl ? window.voyadoSafeUrl(cfg.image) : cfg.image) : '';
      if (safeImg) {
        if (bannerImg.getAttribute('src') !== safeImg) bannerImg.src = safeImg;
        bannerWrap.hidden = false;
      } else {
        bannerWrap.hidden = true;
      }
    }
    const subtitle = ref('subtitle');
    if (subtitle) {
      // Prefer the server-rendered market price (data-gift-wrap-fee = settings.gift_product.price | money,
      // which Shopify converts to the active market's currency). The app config's fee is base-currency
      // only, so its value is right just on the base market. Fall back to it only when unset.
      const dlg = $dialog();
      const serverFee = dlg && dlg.getAttribute('data-gift-wrap-fee');
      const feeStr = serverFee || (cfg && cfg.fee && cfg.fee.price ? money(cfg.fee.price) : '');
      // innerHTML: the server fee (data-gift-wrap-fee) may carry the AED dirham glyph markup. The label and
      // fee are theme-controlled (settings.gift_product price), so there's no user input to sanitise.
      subtitle.innerHTML = `Gifting entire order${feeStr ? ` (${feeStr})` : ''}`;
      subtitle.hidden = false;
    }

    // Fields — toggle each enabled/required + set label + (message) max-length from config.
    Object.keys(FIELD_IDS).forEach((key) => {
      const input = fieldEl(key);
      if (!input) return;
      const wrap = input.closest('[data-input-wrapper]');
      const fcfg = cfg && cfg.fields.filter((f) => f.key === key)[0];
      const enabled = fcfg ? fcfg.enabled : true;
      if (wrap) wrap.hidden = !enabled;
      input.disabled = !enabled;
      if (fcfg && fcfg.required && enabled) input.setAttribute('required', ''); else input.removeAttribute('required');
      const label = wrap && wrap.querySelector('label');
      if (label && fcfg && fcfg.label) label.textContent = fcfg.label;
      if (key === 'message' && fcfg && fcfg.maxlength) {
        input.setAttribute('maxlength', String(fcfg.maxlength));
        const counterMax = wrap && wrap.querySelector('[data-char-counter]');
        if (counterMax) {
          const cnt = counterMax.querySelector('[data-char-count]');
          counterMax.textContent = '';
          if (cnt) counterMax.appendChild(cnt);
          counterMax.append(`/${fcfg.maxlength}`);
        }
      }
    });

    // Actions
    const save = ref('save');
    if (save) {
      const st = save.querySelector('[data-gift-wrap-save-text]') || save;
      st.textContent = (cfg && cfg.labels && cfg.labels.save) || LABELS.save;
    }
    const removeBtn = ref('remove');
    if (removeBtn) {
      const rt = removeBtn.querySelector('[data-gift-wrap-remove-text]');
      if (rt) rt.textContent = (cfg && cfg.labels && cfg.labels.remove) || 'Remove';
    }
  }

  function readFields() {
    const out = {};
    Object.keys(FIELD_IDS).forEach((key) => {
      const input = fieldEl(key);
      if (input && !input.disabled) out[key] = input.value || '';
    });
    return out;
  }
  function prefill(fields) {
    Object.keys(FIELD_IDS).forEach((key) => {
      const input = fieldEl(key);
      if (input) {
        input.value = (fields && fields[key]) || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }
  // Auto-size the message textarea to its content. input.js expands it on 'input', but it initialises while the
  // <dialog> is display:none (scrollHeight 0 → clamped), so we recompute once the dialog is visible on open.
  function growMessage() {
    const ta = fieldEl('message');
    if (ta && ta.tagName === 'TEXTAREA') { ta.style.height = 'auto'; ta.style.height = `${ta.scrollHeight}px`; }
  }

  // Save gate — the purple Save pill stays disabled until every enabled field is valid (mirrors the wishlist
  // share drawer). Reads validity per field (no checkValidity() call, so it never fires `invalid` events that
  // would surface errors prematurely). action-pill's disabled state is disabled + aria-disabled="true".
  function formValid() {
    let ok = true;
    Object.keys(FIELD_IDS).forEach((key) => {
      const input = fieldEl(key);
      if (input && !input.disabled && !input.validity.valid) ok = false;
    });
    return ok;
  }
  function syncSaveGate() {
    const save = ref('save');
    if (!save) return;
    const valid = formValid();
    save.disabled = !valid;
    if (valid) save.removeAttribute('aria-disabled'); else save.setAttribute('aria-disabled', 'true');
  }

  // ── open / close ───────────────────────────────────────────────────────────
  function openDialog(mode, opts) {
    const d = $dialog();
    if (!d) return;
    opts = opts || {};
    ctx = { mode: mode, editing: !!opts.editing, trigger: opts.trigger || null };
    applyConfig();
    // Clear stale validation state so a prior failed validation doesn't persist on re-open. form-validation.js
    // shows an error as BOTH the [data-error-message] text AND aria-invalid on the field (which drives the red
    // border/label via peer-aria-invalid:*), so reset both — clearing only the text left the field red.
    d.querySelectorAll('[data-error-message]').forEach((el) => { el.textContent = ''; });
    d.querySelectorAll('[aria-invalid]').forEach((el) => { el.removeAttribute('aria-invalid'); });
    prefill(opts.fields || {});
    // Reset Remove's enabled state when showing it for the edit state — a prior in-flight remove may have left
    // it disabled/aria-disabled, and the freshly re-rendered launcher re-opens without a page reload.
    const remove = ref('remove');
    if (remove) { remove.hidden = !opts.editing; if (opts.editing) { remove.disabled = false; remove.removeAttribute('aria-disabled'); } }
    syncSaveGate();

    lastFocus = opts.trigger || document.activeElement;
    // The order drawer always opens over the open mini-bag → transparent backdrop (the mini-bag already dims).
    d.setAttribute('data-gift-wrap-nested', '');
    if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    const sc = d.querySelector('[data-drawer-scroll]'); if (sc) sc.scrollTop = 0; // open from the top
    // Lock page scroll while the drawer is open (mirrors the shared menu-drawer / account-addresses pattern).
    document.documentElement.setAttribute('data-scroll-lock', '');
    if (window.createFocusTrap) { trap = window.createFocusTrap(d); trap.activate(); }
    const close = ref('close');
    if (close) close.focus();
    // Size the message textarea now that the dialog is visible (scrollHeight is meaningful).
    if (window.requestAnimationFrame) requestAnimationFrame(growMessage); else growMessage();
  }
  function closeDialog() {
    const d = $dialog();
    if (!d) return;
    if (trap) { trap.deactivate(); trap = null; }
    if (d.close) d.close(); else d.removeAttribute('open');
    // Release the page scroll-lock ONLY if the mini-bag isn't still open beneath the drawer (boolean, not a
    // counter — blindly removing it would unlock the page under the still-open mini-bag).
    if (!miniBagOpen()) document.documentElement.removeAttribute('data-scroll-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    ctx = null;
  }

  // ── save / remove ──────────────────────────────────────────────────────────
  function afterCartChange() { document.dispatchEvent(new CustomEvent('wrapped:cart-updated')); }
  function announce(msg) {
    const el = document.querySelector('[data-gift-wrap-announce]');
    if (el) el.textContent = msg;
  }
  const warn = (e) => { if (window.console && console.warn) console.warn(e && e.message ? e.message : e); };
  function reenableSave() {
    const s = ref('save');
    if (s) { s.disabled = false; s.removeAttribute('aria-disabled'); }
  }

  function doSave() {
    const G = engine();
    if (!G || !ctx || inFlight) return;
    inFlight = true;
    // Disable Save immediately so a second click can't double-submit while the cart write is in flight.
    const save = ref('save');
    if (save) { save.disabled = true; save.setAttribute('aria-disabled', 'true'); }
    const fields = readFields();
    // Drive the co-located app block's saveGifting, which writes the cart directly.
    // Snapshot the block resolver so a dialog re-open (which reassigns ctx) can't repoint this in-flight op.
    const resolveBlock = blockResolver(ctx.trigger);
    withTimeout(driveWithRetry((block) => G.appSaveGifting(block, fields), resolveBlock), OP_TIMEOUT_MS)
      .then(() => { closeDialog(); afterCartChange(); announce('Gift saved'); })
      .catch((e) => { warn(e); reenableSave(); })
      .finally(() => { inFlight = false; });
  }
  function doRemove() {
    const G = engine();
    if (!G || !ctx || inFlight) return;
    inFlight = true;
    // Disable Remove immediately so a second click can't double-submit while the cart write is in flight.
    const removeBtn = ref('remove');
    if (removeBtn) removeBtn.disabled = true;
    const resolveBlock = blockResolver(ctx.trigger);
    withTimeout(driveWithRetry((block) => G.appRemoveGifting(block), resolveBlock), OP_TIMEOUT_MS)
      .then(() => { closeDialog(); afterCartChange(); announce('Gift removed'); })
      .catch((e) => { warn(e); if (removeBtn) removeBtn.disabled = false; })
      .finally(() => { inFlight = false; });
  }

  // ── app-proxy integration ──────────────────────────────────────────────────
  // A resolver for the app's <wrapped-gift-block> for the order gift, snapshotted at call time so a later
  // dialog re-open (which reassigns the module-level `ctx`) can't repoint an already in-flight op: the block
  // the app injected inside this launcher's mini-bag anchor (.cart-gift-wrap-btn).
  function blockResolver(trigger) {
    const G = engine();
    return () => {
      const anchor = trigger && trigger.closest ? trigger.closest('.cart-gift-wrap-btn') : null;
      return G.appBlock('order', anchor);
    };
  }
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  // Settle a promise no later than `ms`: an app op that hangs (block mid-hydration, network stall) still
  // rejects, so the drawer never sticks open with a disabled button and no error ever surfaced.
  function withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('gift-wrap: operation timed out')), ms);
      promise.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
    });
  }
  // Drive an app-block op (save/remove) with a short retry. A freshly re-injected <wrapped-gift-block> (after a
  // cart re-render) can be momentarily un-hydrated — its internal gift state is null, so the app's saveGifting/
  // removeGifting throws — which bites on a rapid add→edit/remove. On failure we wait, re-resolve the block
  // (it hydrates in place), and retry a couple of times before giving up.
  function driveWithRetry(runWithBlock, resolveBlock, tries) {
    tries = tries || 3;
    return runWithBlock(resolveBlock()).catch((e) => {
      if (tries <= 1) return Promise.reject(e);
      return delay(250).then(() => driveWithRetry(runWithBlock, resolveBlock, tries - 1));
    });
  }

  // ── launcher button (Liquid-rendered; opened via delegation) ───────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-gift-wrap-open]');
    if (!btn) return;
    e.preventDefault();
    const mode = btn.getAttribute('data-gift-wrap-mode') || 'order';
    const opts = { editing: btn.getAttribute('data-gift-wrap-state') === 'edit', trigger: btn };
    if (opts.editing) opts.fields = readLineGiftFields(btn);
    openDialog(mode, opts);
  });

  // Saved fields for prefill on Edit — read from the Liquid-rendered launcher's data attributes. The mini-bag
  // (snippets/gift-wrap-button.liquid) renders the launcher's Add/Edit state + saved fields straight from the
  // cart line's Wrapped properties, so there's no JS injection or /cart.js round-trip on re-render.
  const readLineGiftFields = (btn) => ({
    receiver: btn.getAttribute('data-gift-wrap-receiver') || '',
    sender: btn.getAttribute('data-gift-wrap-sender') || '',
    message: btn.getAttribute('data-gift-wrap-message') || ''
  });

  // The order launcher's fee is rendered server-side from settings.gift_product (data-fee-appended). When that
  // setting is empty the Liquid leaves it off, so append "(£X)" from window.Wrapped.options — a light text
  // pass, re-run after the mini-bag re-renders. The Edit label carries no price.
  function appendOrderPrice() {
    const btn = document.querySelector('.cart-gift-wrap-btn [data-gift-wrap-open]');
    if (!btn || btn.getAttribute('data-gift-wrap-state') === 'edit') return;
    const span = btn.querySelector('[data-gift-wrap-price-target]');
    if (!span || span.getAttribute('data-fee-appended') === '1') return;
    const G = engine();
    if (!G) return;
    const fee = G.config('order') && G.config('order').fee && G.config('order').fee.price;
    if (!fee) return;
    span.textContent = `${span.textContent} (${money(fee)})`;
    span.setAttribute('data-fee-appended', '1');
  }

  // ── form submit → validate → save ──────────────────────────────────────────
  function wireForm() {
    const form = ref('form');
    if (!form || form.hasAttribute('data-gift-wrap-wired')) return;
    form.setAttribute('data-gift-wrap-wired', '');
    // form-validation.js validates on submit and dispatches form:valid only when all fields pass.
    form.addEventListener('form:valid', (e) => { e.preventDefault(); doSave(); });
    form.addEventListener('input', syncSaveGate);
    form.addEventListener('change', syncSaveGate);
    const remove = ref('remove');
    if (remove) remove.addEventListener('click', () => { doRemove(); });
    const close = ref('close');
    if (close) close.addEventListener('click', closeDialog);
    const d = $dialog();
    if (d) {
      d.addEventListener('cancel', () => { closeDialog(); });
      // Click on the ::backdrop (outside the panel) closes — the form fills the panel, so a backdrop click
      // lands on the dialog element itself.
      d.addEventListener('click', (e) => { if (e.target === d) closeDialog(); });
    }
  }

  // ── init ───────────────────────────────────────────────────────────────────
  function start() {
    wireForm();
    appendOrderPrice();
    // The drawer launcher is Liquid-rendered by the mini-bag; the app's own launchers stay hidden via CSS
    // (wrapped-gift-block { display:none !important }), so after a cart change we only re-append the order fee
    // to the freshly rendered button.
    ['mini-bag:updated', 'wrapped:cart-updated', 'cart:added'].forEach((ev) => {
      document.addEventListener(ev, appendOrderPrice);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
